import {
  Box,
  Button,
  ButtonGroup,
  IconButton,
  Menu,
  MenuItem,
  Radio,
  Stack,
  Typography,
  useColorScheme,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import CopyIcon from '@mui/icons-material/ContentCopy';
import DeselectIcon from '@mui/icons-material/Deselect';
import SelectAllIcon from '@mui/icons-material/SelectAll';
import AddIcon from '@mui/icons-material/Add';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ComputerIcon from '@mui/icons-material/Computer';
import React from 'react';
import SwerveIcon from '../Icons/Robots/swerve_fill_icon.svg?react';
import { duplicateProject } from '../../utils/projectDuplication';
import { downloadProjects } from '../../utils/projectDownload';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { applicationSlice } from '../../redux/ApplicationSlice';
import { ApplicationActions, RobotActions } from '../../utils/constants';
import { onAddProject, onChangeMultiSelect, onSelectProjects, projectSlice } from '../../redux/ProjectSlice';
import { RootState } from '../../redux/Store';
import { getNewProjectDialog } from '../../utils/newProjectDialog';
import { Project } from './projects';

const ProgramControls = () => {
  const theme = useTheme();
  const isLgOrSmaller = useMediaQuery(theme.breakpoints.down('lg'));
  const isSm = useMediaQuery(theme.breakpoints.down('sm'));
  const { mode, systemMode } = useColorScheme();
  const colorMode = mode === 'system' ? systemMode : mode;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { UPLOAD_FILE, DELETE_PROJECT, LOAD_FROM_SWERVE } = ApplicationActions.Dialogs;
  const { CONNECTED_DRAWER } = ApplicationActions.Drawer;

  const { sortOrder, projects, projectSelect } = useSelector((state: RootState) => state.project);
  const robotState = useSelector((state: RootState) => state.robot.robotState);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [loadMenuAnchor, setLoadMenuAnchor] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const loadMenuOpen = Boolean(loadMenuAnchor);

  const handleCreateNewProject = () => {
    dispatch(applicationSlice.actions.onOpenDialog(getNewProjectDialog()));
  };

  const handleOpenUploadFileDialog = () => {
    setLoadMenuAnchor(null);
    dispatch(applicationSlice.actions.onOpenDialog(UPLOAD_FILE));
  };

  const handleLoadFromSwerve = () => {
    setLoadMenuAnchor(null);
    dispatch(applicationSlice.actions.onOpenDialog(LOAD_FROM_SWERVE));
    const isConnected = robotState === RobotActions.STATE.CONNECTED;
    if (!isConnected) {
      dispatch(projectSlice.actions.onOpenDrawer(CONNECTED_DRAWER));
    }
  };

  const handleLoadProjectClick = (event: React.MouseEvent<HTMLElement>) => {
    setLoadMenuAnchor(event.currentTarget);
  };

  const handleLoadMenuClose = () => {
    setLoadMenuAnchor(null);
  };
  const handleOpenDeleteProjectWarningDialog = () => {
    dispatch(applicationSlice.actions.onOpenDialog(DELETE_PROJECT));
  };
  const handleSelectAll = () => {
    dispatch(onSelectProjects(projects)); // Select all projects
  };
  const handleDeselectAll = () => {
    dispatch(onSelectProjects([]));
  };
  const handleDuplicateSelected = () => {
    projectSelect.selectedProjects.forEach((project: Project) => {
      const copyName = t('projects.copyOf', { name: project.name });
      const duplicate = duplicateProject(project, copyName);
      dispatch(onAddProject(duplicate));
    });
    handleMenuClose();
    clearSelection();
  };
  const handleDownloadSelected = () => {
    void downloadProjects(projectSelect.selectedProjects).catch((error) => {
      console.error('Error downloading projects:', error);
    });
    clearSelection();
  };
  const handleDeleteSelected = () => {
    handleOpenDeleteProjectWarningDialog();
  };
  const clearSelection = () => {
    dispatch(onChangeMultiSelect(false));
    dispatch(onSelectProjects([]));
  };
  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  const handleEditClick = () => {
    dispatch(onChangeMultiSelect(true));
  };
  const handleSortChange = (order: string) => {
    dispatch(projectSlice.actions.onSetSortOrder(order));
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 4, mt: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center" useFlexGap>
          {!isSm ? (
            <>
              <Button
                onClick={handleCreateNewProject}
                variant="contained"
                color="primary"
                size="large"
                sx={{
                  display: 'flex',
                  padding: '8px 22px',
                  justifyContent: 'center',
                  alignItems: 'center',
                  boxShadow: '0px 1px 5px 0px rgba(0, 0, 0, 0.12), 0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 3px 1px -2px rgba(0, 0, 0, 0.20)',
                  borderRadius: '8px',
                  boxSizing: 'border-box',
                }}
                startIcon={<AddIcon />}
              >
                <Typography variant="buttonText">
                  <Trans i18nKey="projectControls.button.create-new-project">Create New Project</Trans>
                </Typography>
              </Button>
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={handleLoadProjectClick}
                endIcon={<KeyboardArrowDownIcon />}
                sx={{
                  display: 'flex',
                  padding: '8px 22px',
                  justifyContent: 'center',
                  alignItems: 'center',
                  boxShadow: '0px 1px 5px 0px rgba(0, 0, 0, 0.12), 0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 3px 1px -2px rgba(0, 0, 0, 0.20)',
                  borderRadius: '8px',
                  boxSizing: 'border-box',
                }}
                startIcon={<FolderOpenIcon />}
              >
                <Typography variant="buttonText">
                  <Trans i18nKey="projectControls.button.load-project">Load Project</Trans>
                </Typography>
              </Button>
              <Menu
                anchorEl={loadMenuAnchor}
                open={loadMenuOpen}
                onClose={handleLoadMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                MenuListProps={{ 'aria-labelledby': 'load-project-button' }}
              >
                <MenuItem onClick={handleOpenUploadFileDialog}>
                  <ComputerIcon fontSize="small" sx={{ mr: 1 }} />
                  <Trans i18nKey="projectControls.loadMenu.load-from-computer">Load from Computer</Trans>
                </MenuItem>
                <MenuItem onClick={handleLoadFromSwerve}>
                  <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                    <SwerveIcon
                      style={{
                        width: 20,
                        height: 20,
                        color: colorMode === 'dark' ? theme.vars?.palette?.common?.white : theme.vars?.palette?.primary?.main,
                      }}
                    />
                  </Box>
                  <Trans i18nKey="projectControls.loadMenu.load-from-swerve">Load from Swerve Drive Robot</Trans>
                </MenuItem>
              </Menu>
            </>
          ) : (
            <>
              <Button
                onClick={handleCreateNewProject}
                variant="contained"
                color="primary"
                size="large"
                sx={{
                  display: 'flex',
                  padding: '8px 22px',
                  justifyContent: 'center',
                  alignItems: 'center',
                  boxShadow: '0px 1px 5px 0px rgba(0, 0, 0, 0.12), 0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 3px 1px -2px rgba(0, 0, 0, 0.20)',
                  borderRadius: '8px',
                }}
              >
                <AddIcon />
              </Button>
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={handleLoadProjectClick}
                endIcon={<KeyboardArrowDownIcon />}
                sx={{
                  display: 'flex',
                  padding: '8px 22px',
                  justifyContent: 'center',
                  alignItems: 'center',
                  boxShadow: '0px 1px 5px 0px rgba(0, 0, 0, 0.12), 0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 3px 1px -2px rgba(0, 0, 0, 0.20)',
                  borderRadius: '8px',
                }}
              >
                <FolderOpenIcon />
              </Button>
              <Menu
                anchorEl={loadMenuAnchor}
                open={loadMenuOpen}
                onClose={handleLoadMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              >
                <MenuItem onClick={handleOpenUploadFileDialog}>
                  <ComputerIcon fontSize="small" sx={{ mr: 1 }} />
                  <Trans i18nKey="projectControls.loadMenu.load-from-computer">Load from Computer</Trans>
                </MenuItem>
                <MenuItem onClick={handleLoadFromSwerve}>
                  <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                    <SwerveIcon
                      style={{
                        width: 20,
                        height: 20,
                        color: colorMode === 'dark' ? theme.vars?.palette?.common?.white : theme.vars?.palette?.primary?.main,
                      }}
                    />
                  </Box>
                  <Trans i18nKey="projectControls.loadMenu.load-from-swerve">Load from Swerve Drive Robot</Trans>
                </MenuItem>
              </Menu>
            </>
          )}
        </Stack>
        <Stack direction="row" spacing={2} alignItems="center" useFlexGap>
          <Box sx={{ flexGrow: 1 }} />
          {projectSelect.isMultiSelect ? (
            <>
              {isLgOrSmaller ? (
                <ButtonGroup>
                  <IconButton onClick={handleSelectAll} aria-label={t('aria-label.select-all')}>
                    <SelectAllIcon />
                  </IconButton>
                  <IconButton onClick={handleDeselectAll} aria-label={t('aria-label.deselect-all')}>
                    <DeselectIcon />
                  </IconButton>
                  <IconButton
                    onClick={handleDuplicateSelected}
                    disabled={projectSelect.selectedProjects.length === 0}
                    aria-label={t('aria-label.duplicate')}
                  >
                    <CopyIcon />
                  </IconButton>
                  <IconButton
                    onClick={handleDownloadSelected}
                    disabled={projectSelect.selectedProjects.length === 0}
                    aria-label={t('aria-label.download')}
                  >
                    <DownloadIcon />
                  </IconButton>
                  <IconButton
                    onClick={handleDeleteSelected}
                    disabled={projectSelect.selectedProjects.length === 0}
                    aria-label={t('aria-label.delete')}
                  >
                    <DeleteIcon />
                  </IconButton>
                  <IconButton onClick={clearSelection} aria-label={t('aria-label.cancel')}>
                    <CancelIcon />
                  </IconButton>
                </ButtonGroup>
              ) : (
                <ButtonGroup variant="text">
                  <Button onClick={handleSelectAll}>
                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                      <SelectAllIcon />
                      <Typography noWrap variant="buttonText">
                        <Trans i18nKey="projectControls.button.select-all">Select All</Trans>
                      </Typography>
                    </Stack>
                  </Button>
                  <Button onClick={handleDeselectAll}>
                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                      <DeselectIcon />
                      <Typography noWrap variant="buttonText">
                        <Trans i18nKey="projectControls.button.deselect-all">Deselect All</Trans>
                      </Typography>
                    </Stack>
                  </Button>
                  <Button onClick={handleDuplicateSelected} disabled={projectSelect.selectedProjects.length === 0}>
                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                      <CopyIcon />
                      <Typography variant="buttonText">
                        <Trans i18nKey="projectControls.button.duplicate">Duplicate</Trans>
                      </Typography>
                    </Stack>
                  </Button>
                  <Button onClick={handleDownloadSelected} disabled={projectSelect.selectedProjects.length === 0}>
                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                      <DownloadIcon />
                      <Typography variant="buttonText">
                        <Trans i18nKey="projectControls.button.download">Download</Trans>
                      </Typography>
                    </Stack>
                  </Button>
                  <Button onClick={handleDeleteSelected} disabled={projectSelect.selectedProjects.length === 0}>
                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                      <DeleteIcon />
                      <Typography variant="buttonText">
                        <Trans i18nKey="projectControls.button.delete">Delete</Trans>
                      </Typography>
                    </Stack>
                  </Button>
                  <Button onClick={clearSelection}>
                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                      <CancelIcon />
                      <Typography variant="buttonText">
                        <Trans i18nKey="projectControls.button.cancel">Cancel</Trans>
                      </Typography>
                    </Stack>
                  </Button>
                </ButtonGroup>
              )}
            </>
          ) : (
            <ButtonGroup variant="text">
              <Button onClick={handleMenuClick}>
                <Typography variant="buttonText">
                  <Trans i18nKey="projectControls.button.sort">Sort</Trans>
                </Typography>
              </Button>
              <Button onClick={handleEditClick}>
                <Typography variant="buttonText">
                  <Trans i18nKey="projectControls.button.edit">Edit</Trans>
                </Typography>
              </Button>
            </ButtonGroup>
          )}
        </Stack>
      </Stack>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        MenuListProps={{
          'aria-labelledby': t('aria-label.sort-menu'),
        }}
        PaperProps={{
          style: {
            borderRadius: '8px',
            width: '25ch',
            boxShadow: '0px 3px 14px 2px rgba(0, 0, 0, 0.12), 0px 8px 10px 1px rgba(0, 0, 0, 0.14), 0px 5px 5px -3px rgba(0, 0, 0, 0.20)',
          },
        }}
      >
        <MenuItem onClick={() => handleSortChange('newest')} selected={sortOrder === 'newest'}>
          <Radio checked={sortOrder === 'newest'} color="primary" />
          <Trans i18nKey={'projectControls.sortMenu.date-newest'}>Date Newest</Trans>
        </MenuItem>
        <MenuItem onClick={() => handleSortChange('oldest')} selected={sortOrder === 'oldest'}>
          <Radio checked={sortOrder === 'oldest'} color="primary" />
          <Trans i18nKey={'projectControls.sortMenu.date-oldest'}>Date Oldest</Trans>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default ProgramControls;
